import { MouseEvent, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { useI18n } from "i18n";
import { selectCurrentEventId } from "../event/selectors";
import useNotificationActions from "../notification/hooks/useNotificationActions";
import useLobbyHostFirestore from "./hooks/useLobbyHostFirestore";
import { toggleLobbyBanner } from "./redux/slice";
import { ILobbyGuest } from "./types";
import { useStyles } from "./styles";

interface Props {
  guest: ILobbyGuest;
}

const LobbyAdmitButton = ({ guest }: Props): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const [isAdmitting, setIsAdmitting] = useState(false);
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { admitGuestIntoEvent } = useLobbyHostFirestore();
  const eventId = useSelector(selectCurrentEventId);
  const { t } = useI18n(["common", "customFloorPlan"]);

  const admitGuest = async (e: MouseEvent) => {
    e.stopPropagation();

    if (!eventId) {
      return;
    }
    setIsAdmitting(true);
    const response = await admitGuestIntoEvent(eventId, guest.userId);

    if (response) {
      addErrorNotification({
        message: t("customFloorPlan:unable.admit.lobby", {
          key1: guest.name,
          key2: response,
        }),
      });
    } else {
      addSuccessNotification({
        message: t("customFloorPlan:user.admitted.event", {
          key: guest.name,
        }),
      });
    }
    setIsAdmitting(false);
    dispatch(toggleLobbyBanner(false));
  };

  if (isAdmitting) {
    return <Loader data-testid="lobby-admit-loader" size={24} />;
  }

  return (
    <Button
      variant="secondary"
      color="blue"
      onClick={admitGuest}
      data-testid="lobby-admit-btn"
      size="sm"
      contentClassName={styles.lobbyAdmitButton}
      style={{
        whiteSpace: "nowrap",
        padding: "5px 30px",
        width: "101px",
        height: "28px",
      }}
    >
      {t("admit")}
    </Button>
  );
};

export default LobbyAdmitButton;
